import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useReducedMotion } from 'motion/react'
import type { Variants } from 'motion/react'
import * as m from 'motion/react-m'

import {
  AtSignIcon,
  ChartColumnIcon,
  EmptyState,
  PlusIcon,
  SquarePenIcon,
} from '@buffer-mono/popcorn'

import { useCanPostOnAnyChannel } from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'

import {
  EmptyStateExplainerCard,
  EmptyStateExploreCards,
  ExploreActionCard,
  ExploreLinkCard,
} from '../../components/EmptyStates'
import { FeedRow } from '../../components/FeedRow'
import type { MentionsEmptyStateChannel } from './types'

import styles from './MentionsListeningEmptyState.module.css'

const EASE_OUT_CUBIC = [0.215, 0.61, 0.355, 1] as const
const EASE_IN_OUT_CUBIC = [0.645, 0.045, 0.355, 1] as const

const RING_COUNT = 5
const RING_DURATION_S = 2.6
const RING_STAGGER_S = 0.5
const BOB_DURATION_S = 2.8
const BOB_PEAK_PX = -4
const SETTLE_DURATION_S = 0.7

// Intro plays once. Total length is bound by the last ring finishing:
// (RING_COUNT - 1) * stagger + ring duration = 4 * 0.5 + 2.6 = 4.6s
const INTRO_DURATION_MS = 5000

const cardVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', bounce: 0.2, visualDuration: 0.4 },
  },
}

const bobVariants: Variants = {
  rest: {
    y: 0,
    transition: { duration: SETTLE_DURATION_S, ease: EASE_OUT_CUBIC },
  },
  intro: {
    y: [0, BOB_PEAK_PX, 0],
    transition: { duration: BOB_DURATION_S, ease: EASE_IN_OUT_CUBIC },
  },
  hover: {
    y: [0, BOB_PEAK_PX, 0],
    transition: {
      duration: BOB_DURATION_S,
      ease: EASE_IN_OUT_CUBIC,
      repeat: Infinity,
    },
  },
}

const ringVariants: Variants = {
  // Rest mirrors the natural end-of-cycle state (fully expanded, invisible).
  // On unhover, a mid-flight ring continues outward and fades instead of
  // snapping back inward.
  rest: {
    scale: 2.1,
    opacity: 0,
    transition: { duration: SETTLE_DURATION_S, ease: EASE_OUT_CUBIC },
  },
  intro: (i: number) => ({
    scale: [1, 2.1],
    opacity: [0.55, 0],
    transition: {
      duration: RING_DURATION_S,
      delay: i * RING_STAGGER_S,
      ease: EASE_OUT_CUBIC,
    },
  }),
  hover: (i: number) => ({
    scale: [1, 2.1],
    opacity: [0.55, 0],
    transition: {
      duration: RING_DURATION_S,
      delay: i * RING_STAGGER_S,
      ease: EASE_OUT_CUBIC,
      repeat: Infinity,
    },
  }),
}

type MentionsListeningEmptyStateProps = {
  /**
   * Channels in scope. Only the count matters: one channel lets the message
   * point at it, several keep it generic.
   */
  channels?: MentionsEmptyStateChannel[]
}

/**
 * Shown when the channels in scope support mentions but have never had one.
 * Buffer does not import mentions from before a channel was connected, so an
 * account with a long mention history still starts empty here — the explainer
 * says so, rather than leaving the user to conclude the inbox is broken.
 */
export const MentionsListeningEmptyState = ({
  channels = [],
}: MentionsListeningEmptyStateProps = {}): JSX.Element => {
  const prefersReducedMotion = useReducedMotion()
  const canPostOnAnyChannel = useCanPostOnAnyChannel()
  const canConnectChannels = useHasOrganizationAction('manageChannels')
  const [phase, setPhase] = useState<'intro' | 'rest'>('intro')
  const [isHovering, setIsHovering] = useState(false)

  useEffect(() => {
    if (prefersReducedMotion) {
      setPhase('rest')
      return
    }
    const timer = window.setTimeout(() => {
      setPhase('rest')
    }, INTRO_DURATION_MS)
    return (): void => {
      window.clearTimeout(timer)
    }
  }, [prefersReducedMotion])

  // If the user hovers during the intro, drop straight to the resting state so
  // unhover transitions back to rest rather than restarting the intro burst.
  const handleHoverStart = useCallback(() => {
    setIsHovering(true)
    setPhase('rest')
  }, [])

  const handleHoverEnd = useCallback(() => {
    setIsHovering(false)
  }, [])

  const iconAnimateState = prefersReducedMotion
    ? 'rest'
    : isHovering
      ? 'hover'
      : phase

  return (
    <FeedRow>
      <div className={styles.layoutWrapper}>
        <m.div
          className={styles.card}
          variants={cardVariants}
          // Starting at `visible` skips the staggered fade and 8px rise, which
          // the ring and bob settings alone do not cover.
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          animate="visible"
          onHoverStart={prefersReducedMotion ? undefined : handleHoverStart}
          onHoverEnd={prefersReducedMotion ? undefined : handleHoverEnd}
        >
          <div className={styles.stack}>
            <EmptyState
              size="large"
              variant="primary"
              className={styles.emptyState}
            >
              <m.div variants={itemVariants}>
                <EmptyState.Icon className={styles.iconContainer}>
                  <div className={styles.iconAnimator}>
                    {Array.from({ length: RING_COUNT }, (_, i) => (
                      <m.span
                        key={i}
                        className={styles.ping}
                        variants={ringVariants}
                        custom={i}
                        initial="rest"
                        animate={iconAnimateState}
                        aria-hidden
                      />
                    ))}
                    <m.span
                      className={styles.atSign}
                      variants={bobVariants}
                      initial="rest"
                      animate={iconAnimateState}
                    >
                      <AtSignIcon />
                    </m.span>
                  </div>
                </EmptyState.Icon>
              </m.div>
              <m.div variants={itemVariants}>
                <EmptyState.Heading>
                  We&apos;re listening for new mentions
                </EmptyState.Heading>
              </m.div>
              <m.div variants={itemVariants}>
                <EmptyState.Description>
                  {channels.length === 1
                    ? "We'll let you know when someone mentions this account."
                    : "We'll let you know when someone mentions one of your connected accounts."}
                </EmptyState.Description>
              </m.div>
            </EmptyState>

            <m.div variants={itemVariants} className={styles.section}>
              <EmptyStateExplainerCard
                heading="Can't see all your past mentions? Don't worry!"
                body="When you connect a new channel, we don't import any existing mentions. We check for new mentions from now on."
              />
            </m.div>

            <m.div variants={itemVariants} className={styles.section}>
              <EmptyStateExploreCards>
                <ExploreLinkCard
                  to="/insights"
                  icon={<ChartColumnIcon size="small" />}
                  title="Discover Insights"
                  description="Learn about who interacts with your posts and how to improve your engagement."
                />
                {canPostOnAnyChannel && (
                  <NewPostComposerTrigger cta="publish-community-listeningForMentions-newPost-1">
                    <ExploreActionCard
                      icon={<SquarePenIcon size="small" />}
                      title="Post something new"
                      description="Use templates to create content that starts conversations. ✨ Pro tip: Ask a question"
                    />
                  </NewPostComposerTrigger>
                )}
                {canConnectChannels && (
                  <ExploreActionCard
                    icon={<PlusIcon size="small" />}
                    title="Connect more channels"
                    description="Manage conversations from all your channels from a single, distraction-free inbox."
                    onClick={(): void => {
                      window?.appshell?.actions?.openChannelStorefront({
                        cta: 'publish-community-listeningForMentions-connectChannel-1',
                      })
                    }}
                  />
                )}
              </EmptyStateExploreCards>
            </m.div>
          </div>
        </m.div>
      </div>
    </FeedRow>
  )
}
